import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Typography, Tag, Button, Image, Tooltip, message } from 'antd';
import { OrderedListOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import CrudPage from '../../../components/common/CrudPage';
import { utilityBannerService } from '../../../services/next';
import UtilityBannerOrderModal from './components/UtilityBannerOrderModal';

const { Text } = Typography;

// Chuyển từ cms.next.lichviet.org/utilities-banner (UtilitiesBannerController). Bảng
// `lichviet.utilities_banner`; mọi thay đổi bump cache_api 34 ở API.

const STATUS_OPTIONS = [
  { label: 'Đã duyệt', value: 1 },
  { label: 'Chờ duyệt', value: 0 },
];

const IS_DEFAULT_OPTIONS = [
  { label: 'Có', value: 1 },
  { label: 'Không', value: 0 },
];

const DEEPLINK_DOC_URL = 'https://docs.google.com/spreadsheets/d/1Rj7vlAhIiSjU6Fx8hum3GzI8cxD6Mdw5sKeFgGqkxag/edit#gid=908194191';

const QUERY_KEY = 'utilityBannerList';

const QuanLyTienIch = () => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [orderOpen, setOrderOpen] = useState(false);

  const { data: metaRes, isError: isMetaError } = useQuery({
    queryKey: ['utilityBannerMetadata'],
    queryFn: utilityBannerService.getMetadata,
    staleTime: 5 * 60 * 1000,
  });

  // Metadata cần quyền riêng ("Metadata" của màn Tiện ích): thiếu thì dropdown Vị trí/Danh mục
  // rỗng mà không lưu được (Vị trí bắt buộc) — phải nói rõ lý do.
  useEffect(() => {
    if (isMetaError) {
      message.warning(
        'Không tải được danh sách Vị trí / Danh mục. Tài khoản có thể thiếu quyền "Metadata" của màn Tiện ích — liên hệ quản trị để được cấp.',
        6
      );
    }
  }, [isMetaError]);

  const positionOptions = useMemo(() => metaRes?.data?.positions || [], [metaRes]);
  const categoryOptions = useMemo(() => metaRes?.data?.categories || [], [metaRes]);
  const defaultPosition = metaRes?.data?.defaultPosition;

  // Modal "Thứ tự & tiêu đề mục" mở sẵn đúng vị trí đang lọc ở ô "Trang hiển thị" (CrudPage giữ
  // bộ lọc trên URL `?position=`); chưa lọc hoặc vị trí không còn trong metadata -> Trang chủ.
  const filterPosition = searchParams.get('position');
  const orderInitialPosition = positionOptions.some((p) => p.value === filterPosition)
    ? filterPosition
    : defaultPosition;

  const positionLabel = useMemo(() => {
    const map = {};
    positionOptions.forEach((p) => {
      map[p.value] = p.label;
    });
    return map;
  }, [positionOptions]);

  const categoryLabel = useMemo(() => {
    const map = {};
    categoryOptions.forEach((c) => {
      map[c.value] = c.label;
    });
    return map;
  }, [categoryOptions]);

  const handleOrderSaved = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['utilityBannerMetadata'] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
  }, [queryClient]);

  const columns = useMemo(() => [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
    { title: 'TÊN TIỆN ÍCH', dataIndex: 'name', key: 'name', width: 200, render: (t) => <Text strong>{t}</Text> },
    { title: 'ẢNH', dataIndex: 'icon', key: 'icon', width: 80, render: (img) => (img ? <Image src={img} width={40} height={40} /> : '-') },
    {
      title: 'DEEPLINK',
      dataIndex: 'link',
      key: 'link',
      width: 240,
      render: (t) => (
        <Tooltip title={t}>
          <Text ellipsis style={{ maxWidth: 220 }}>{t}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'VỊ TRÍ',
      dataIndex: 'position',
      key: 'position',
      width: 180,
      render: (p) => (
        <Tooltip title={p}>
          <span>{positionLabel[p] || p}</span>
        </Tooltip>
      ),
    },
    { title: 'DANH MỤC', dataIndex: 'category_id', key: 'category_id', width: 140, render: (c) => (c ? categoryLabel[c] || c : '-') },
    { title: 'VERSION', dataIndex: 'version', key: 'version', width: 90 },
    { title: 'THỨ TỰ', dataIndex: 'place', key: 'place', width: 80 },
    {
      title: 'HIỆN MẶC ĐỊNH',
      dataIndex: 'is_default',
      key: 'is_default',
      width: 120,
      render: (s) => (s === 1 ? <Tag color="blue">Có</Tag> : <Tag>Không</Tag>),
    },
    {
      title: 'TRẠNG THÁI',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (s) => (s === 1 ? <Tag color="green">Đã duyệt</Tag> : <Tag color="orange">Chờ duyệt</Tag>),
    },
    {
      title: 'HẾT HẠN',
      dataIndex: 'end_time',
      key: 'end_time',
      width: 140,
      render: (t) => {
        if (!t) return '-';
        const expired = dayjs(t).isBefore(dayjs());
        const text = dayjs(t).format('DD/MM/YYYY');
        return expired ? <Tag color="red">{text}</Tag> : text;
      },
    },
  ], [positionLabel, categoryLabel]);

  // Thêm mới: chọn nhiều vị trí, mỗi vị trí một dòng (như CMS cũ). Sửa: một dòng = một vị trí.
  // Sao chép (CrudPage mở form Thêm điền sẵn) -> `positions` lấy từ vị trí của dòng gốc.
  const formFields = useCallback(
    ({ isEditMode }) => [
      { name: 'category_id', label: 'Danh mục', type: 'select', options: categoryOptions, span: 12, propsInput: { allowClear: true } },
      isEditMode
        ? { name: 'position', label: 'Vị trí hiển thị', type: 'select', options: positionOptions, span: 12, rules: [{ required: true, message: 'Vui lòng chọn vị trí!' }] }
        : {
            name: 'positions',
            label: 'Vị trí hiển thị',
            type: 'select',
            options: positionOptions,
            span: 12,
            rules: [{ required: true, message: 'Vui lòng chọn vị trí!' }],
            propsInput: { mode: 'multiple', allowClear: true, placeholder: 'Chọn một hoặc nhiều vị trí' },
          },
      { name: 'name', label: 'Tên tiện ích', type: 'input', span: 24, rules: [{ required: true, whitespace: true, message: 'Vui lòng nhập tên!' }] },
      { name: 'icon', label: 'Ảnh', type: 'upload-cdn', span: 24, uploadFolder: 'utilities_banner', enterLink: true, linkPlaceholder: 'Link ảnh', rules: [{ required: true, message: 'Vui lòng chọn ảnh!' }] },
      {
        name: 'link',
        label: 'Deeplink',
        type: 'input',
        span: 24,
        rules: [{ required: true, whitespace: true, message: 'Vui lòng nhập deeplink!' }],
        propsFormItem: {
          extra: (
            <span>
              Format theo file{' '}
              <a target="_blank" rel="noreferrer" href={DEEPLINK_DOC_URL}>
                Lịch Việt - Deeplink
              </a>
            </span>
          ),
        },
      },
      { name: 'version', label: 'Version', type: 'input', span: 12, rules: [{ required: true, whitespace: true, message: 'Vui lòng nhập version!' }] },
      { name: 'is_default', label: 'Hiện mặc định', type: 'select', options: IS_DEFAULT_OPTIONS, defaultValue: 1, span: 6 },
      { name: 'status', label: 'Trạng thái', type: 'select', options: STATUS_OPTIONS, defaultValue: 1, span: 6 },
    ],
    [categoryOptions, positionOptions]
  );

  const mapDataToForm = useCallback(
    (record) => ({ positions: record.position ? [record.position] : [] }),
    []
  );

  const filterFields = useMemo(() => [
    {
      label: 'Trang hiển thị',
      value: 'position',
      options: [{ label: 'Tất cả', value: '' }, ...positionOptions],
    },
    {
      label: 'Danh mục',
      value: 'category_id',
      options: [{ label: 'Tất cả', value: '' }, ...categoryOptions],
    },
    {
      label: 'Hiện mặc định',
      value: 'is_default',
      options: [{ label: 'Tất cả', value: '' }, ...IS_DEFAULT_OPTIONS],
    },
    {
      label: 'Trạng thái',
      value: 'status',
      options: [{ label: 'Tất cả', value: '' }, ...STATUS_OPTIONS],
    },
  ], [positionOptions, categoryOptions]);

  return (
    <>
      <CrudPage
        title="QUẢN LÝ TIỆN ÍCH"
        subTitle="Tiện ích hiển thị theo từng vị trí trên ứng dụng (Trang chủ, tab Tử vi phong thuỷ, Tâm an…)"
        itemsName="Tiện ích"
        queryKey={QUERY_KEY}
        service={utilityBannerService}
        columns={columns}
        formFields={formFields}
        filterFields={filterFields}
        searchPlaceholder="Tìm kiếm theo tên..."
        addButtonLabel="Thêm tiện ích"
        deleteTitle="Xóa tiện ích này?"
        formConfig={{ width: 800 }}
        mapDataToForm={mapDataToForm}
        renderHeaderActions={
          <Button icon={<OrderedListOutlined />} onClick={() => setOrderOpen(true)} disabled={positionOptions.length === 0}>
            Thứ tự & tiêu đề mục
          </Button>
        }
      />
      <UtilityBannerOrderModal
        open={orderOpen}
        onClose={() => setOrderOpen(false)}
        positions={positionOptions}
        initialPosition={orderInitialPosition}
        onSaved={handleOrderSaved}
      />
    </>
  );
};

export default QuanLyTienIch;
